// Stage 2.3 rules shared by the server (which enforces them) and the screens: Sample Management,
// Tech Packs (statuses, revisions, measurement templates, library) and R&D developments — as FALCON PLM v2.0.41.
import type { Round, Workflow } from "./orders.js";
import { normalizePacking, type PackingProfile } from "./items.js";
import { cadFileIds, normalizeCad, type CadDrawing } from "./cad.js";
import { gradeRow } from "./grading.js";

// ---- Sample Management -----------------------------------------------------------------------------------------
export type SampleType = "sample" | "fit" | "pps";
export const SAMPLE_TYPES: Array<[SampleType, string]> = [["sample", "Initial Sample"], ["fit", "Fit Sample"], ["pps", "PPS"]];
export const SAMPLE_TYPE_LABEL: Record<SampleType, string> = { sample: "Initial Sample", fit: "Fit Sample", pps: "PPS" };
export type SampleStatus = "waiting" | "in_transit" | "received" | "under_review" | "approved" | "rejected";
export const SAMPLE_STATUSES: Array<[SampleStatus, string]> = [
  ["waiting", "Requested"], ["in_transit", "Sent"], ["received", "Received"], ["under_review", "In review"], ["approved", "Approved"], ["rejected", "Rejected"],
];
export const SAMPLE_STATUS_LABEL = Object.fromEntries(SAMPLE_STATUSES) as Record<SampleStatus, string>;
export interface SampleRoundLite { type: SampleType; round: number; status: SampleStatus; reviewDate?: string | null; rejectionReason?: string; corrections?: string; comments?: string; reviewedBy?: string }

const roundResult = (s: SampleStatus): Round["status"] => (s === "approved" ? "Approved" : s === "rejected" ? "Rejected" : "Waiting");
/**
 * The Time & Action Plan cells for one sample type, from its rounds (as v2.0.41): the first round fills the
 * 1st cell and the latest round (if more than one) the 2nd cell.
 */
export function workflowFromRounds(w: Workflow, type: SampleType, rounds: SampleRoundLite[]): Workflow {
  const list = rounds.filter((r) => r.type === type).sort((a, b) => a.round - b.round);
  const out: Workflow = { ...w };
  const one = `${type}1` as const, two = `${type}2` as const;
  if (!list.length) { delete out[one]; delete out[two]; return out; }
  const first = list[0], latest = list[list.length - 1];
  const cell = (r: SampleRoundLite): Round => ({
    status: roundResult(r.status),
    ...(r.reviewDate ? { date: r.reviewDate } : {}),
    ...(r.status === "rejected" && (r.rejectionReason || r.corrections || r.comments) ? { note: r.rejectionReason || r.corrections || r.comments, noteBy: r.reviewedBy || "" } : {}),
  });
  out[one] = cell(first);
  if (list.length > 1) out[two] = cell(latest); else delete out[two];
  return out;
}
/** Approved = the latest round of that type is approved. */
export function sampleTypeApproved(rounds: SampleRoundLite[], type: SampleType): boolean {
  const list = rounds.filter((r) => r.type === type).sort((a, b) => a.round - b.round);
  return list[list.length - 1]?.status === "approved";
}
/** Whether a new round of this type may be started (v2.0.41 gating). Returns the reason if not. */
export function newRoundBlocked(rounds: SampleRoundLite[], type: SampleType, itemType: string, bulkStarted: boolean, waivedKinds: SampleType[] = []): string | null {
  if (itemType !== "product") return "Fabric and trim lines skip sampling.";
  if (waivedKinds.includes(type)) return "Waived — STP (Straight To Production).";
  if (bulkStarted) return "Sampling is read-only after Bulk Production has started.";
  if (type === "fit" && !sampleTypeApproved(rounds, "sample") && !waivedKinds.includes("sample")) return "The Initial Sample must be approved first.";
  if (type === "pps" && !sampleTypeApproved(rounds, "fit") && !waivedKinds.includes("fit")) return "The Fit Sample must be approved first.";
  const list = rounds.filter((r) => r.type === type).sort((a, b) => a.round - b.round);
  const latest = list[list.length - 1];
  if (latest && latest.status === "approved") return `${SAMPLE_TYPE_LABEL[type]} is already approved.`;
  if (latest && latest.status !== "rejected") return "Continue the current round before starting another.";
  return null;
}
export function nextSampleAction(rounds: SampleRoundLite[], waivedKinds: SampleType[] = []): string {
  if (!sampleTypeApproved(rounds, "sample") && !waivedKinds.includes("sample")) return "Initial Sample";
  if (!sampleTypeApproved(rounds, "fit") && !waivedKinds.includes("fit")) return "Fit Sample";
  if (!sampleTypeApproved(rounds, "pps") && !waivedKinds.includes("pps")) return "PPS";
  return waivedKinds.length ? "Waived — STP" : "Sampling complete";
}

// ---- Tech Packs --------------------------------------------------------------------------------------------------
export type TechPackStatus = "draft" | "review" | "approved" | "superseded";
export const TECHPACK_STATUSES: Array<[TechPackStatus, string]> = [["draft", "Draft"], ["review", "Internal Review"], ["approved", "Approved"], ["superseded", "Superseded"]];
export const TECHPACK_STATUS_LABEL = Object.fromEntries(TECHPACK_STATUSES) as Record<TechPackStatus, string>;
export const LIBRARY_CATEGORIES = [
  "Main / Woven Label", "Wash Care Label", "Size Label", "Swing Tag", "Heat Transfer Label",
  "FR / Certification Label", "Badge / Patch", "Reflective Tape", "Zip", "Button / Snap",
  "Hook & Loop / Velcro", "Thread", "Elastic", "Drawcord", "Barcode / EAN Label", "Polybag",
  "Carton Label", "Shipping Mark", "Carton Artwork", "Tissue / Insert", "Hanger", "Other Trim / Packaging",
];

export const MEASURE_UNITS = ["cm", "mm", "inch"];
/** A point of measure. Graded rows also keep their base size, the grade (+/- per size), any sizes with their own step
 *  and the sizes typed over by hand; their values are worked out from those (shared/grading.ts). */
export interface MeasureRow { pom: string; tolerance: string; values: Record<string, string>; base?: string; grade?: string; steps?: Record<string, string>; manual?: string[] }
export interface ConstructionRow { area: string; seam: string; spi: string; notes: string }
export interface BrandingRow { artwork: string; method: string; position: string; dimensions: string; pantone: string; artworkId: string; artworkRevision: number }
export interface LibraryAttachment { itemId: string; revision: number; placement: string; qty: string; notes: string; snapshot: { code: string; name: string; category: string; material?: string; dimensions?: string; colour?: string; pantone?: string; instructions?: string; supplier?: string; unit?: string; branding?: string } }
/** One Bill of Materials line: fabric, trim, label, packaging or other. Library lines keep the trim revision they attached. */
export type BomType = "fabric" | "trim" | "label" | "packaging" | "other";
export const BOM_TYPES: Array<[BomType, string]> = [["fabric", "Fabric"], ["trim", "Trim"], ["label", "Label"], ["packaging", "Packaging"], ["other", "Other"]];
export interface BomLine {
  type: BomType; itemId: string; revision: number; code: string; description: string; supplier: string; colour: string; placement: string;
  qty: string; unit: string; cost: string; artworkId: string; artworkRevision: number; notes: string; snapshot: LibraryAttachment["snapshot"] | null;
}
/** What a BOM line shows from its fabric / trim record (picture, description, colour, size, material, supplier). */
export interface BomItemView {
  found: boolean; itemId: string; itemType: "fabric" | "trim" | ""; code: string; name: string;
  description: string; colour: string; size: string; material: string; supplier: string; imageFileId: string; imageMime: string;
  /** Fabrics: the record's specification, so a sealed copy can be shown without composition to people who may not see it. */
  fabricData?: Record<string, unknown>;
  /** Fabrics: the composition is a trade secret (shown with a "Trade secret" tag to people who may see it). */
  secret?: boolean;
}
export interface Colourway { colour: string; pantone: string; notes: string }
export interface Folding { method: string; foldedSize: string; instructions: string }
export const FOLDING_METHODS = ["Flat pack", "Standard fold", "Shirt fold with board", "Trouser fold", "Coverall fold", "Hanging (on hanger)", "Rolled"];
/** The label and packaging designs a Tech Pack uses, pinned to the revision (a PO keeps what the Tech Pack had). */
export type PinnedDesigns = Record<string, { artworkId: string; revision: number }>;
export interface TechPackContent {
  standards: string[]; generalNotes: string; fabricNotes: string; sizes: string[];
  colourways: Colourway[];
  /** The unit of the size spec (points of measure, tolerances): cm, mm or inch. */
  measureUnit: string;
  measurements: MeasureRow[]; construction: ConstructionRow[]; branding: BrandingRow[];
  bom: BomLine[];
  /** The BOM's library lines (kept for older Tech Packs, imports and backups). */
  libraryItems: LibraryAttachment[]; packing: PackingProfile | null; packagingNotes: string;
  labels: PinnedDesigns; folding: Folding;
  /** The wash care label this Tech Pack prints ("" = the garment's own). */
  washCareLabelId: string;
  /** Sealed revisions keep the label data (images and texts) they were approved with; live changes don't alter them. */
  labelSnapshot?: unknown;
  /** Sealed revisions keep the BOM pictures and record details they were approved with (one per BOM line). */
  bomSnapshot?: BomItemView[];
  /** Section 2, CAD & technical drawings. */
  cad: CadDrawing[];
  /** Every file the drawings hold (kept in step with cad; older content listed its "Sketches" here). */
  attachments: string[];   // file ids
}
const str = (v: unknown, max = 2000) => String(v ?? "").slice(0, max);
const LABEL_CATEGORIES = /label|tag|badge|patch|barcode|ean/i, PACK_CATEGORIES = /polybag|carton|shipping|tissue|insert|hanger|packag/i;
/** The BOM type a library category belongs to. */
export const bomTypeFor = (category: string): BomType => (LABEL_CATEGORIES.test(category) ? "label" : PACK_CATEGORIES.test(category) ? "packaging" : "trim");
function snapshotOf(a: any): LibraryAttachment["snapshot"] {
  return { code: str(a?.code, 60), name: str(a?.name, 200), category: str(a?.category, 120), material: str(a?.material, 200), dimensions: str(a?.dimensions, 120), colour: str(a?.colour, 120), pantone: str(a?.pantone, 60), instructions: str(a?.instructions, 1000), supplier: str(a?.supplier, 200), unit: str(a?.unit, 40), branding: str(a?.branding, 400) };
}
function bomLine(r: any): BomLine {
  const snap = r?.snapshot && (r.snapshot.code || r.itemId) ? snapshotOf(r.snapshot) : null;
  const type = BOM_TYPES.some(([k]) => k === r?.type) ? r.type as BomType : bomTypeFor(snap?.category || "");
  return {
    type, itemId: str(r?.itemId, 60), revision: Math.max(0, Math.floor(Number(r?.revision) || 0)),
    code: str(r?.code || snap?.code, 60), description: str(r?.description || snap?.name, 300), supplier: str(r?.supplier ?? snap?.supplier, 200), colour: str(r?.colour ?? snap?.colour, 120),
    placement: str(r?.placement, 200), qty: str(r?.qty, 40), unit: str(r?.unit || snap?.unit, 40), cost: str(r?.cost, 40),
    artworkId: str(r?.artworkId, 60), artworkRevision: Math.max(0, Math.floor(Number(r?.artworkRevision) || 0)), notes: str(r?.notes, 500), snapshot: snap,
  };
}
export function normalizeTechPackContent(c: any): TechPackContent {
  const sizes = Array.isArray(c?.sizes) ? [...new Set(c.sizes.map((x: unknown) => str(x, 20).trim()).filter(Boolean))] as string[] : [];
  const rows = (v: unknown) => (Array.isArray(v) ? v : []);
  // The BOM; library items sent the older way (libraryItems only) join it.
  const bom = rows(c?.bom).map(bomLine).filter((b) => b.code || b.description || b.itemId);
  for (const a of rows(c?.libraryItems)) {
    const id = str(a?.itemId, 60);
    if ((id || a?.snapshot?.code) && !bom.some((b) => b.itemId && b.itemId === id)) bom.push(bomLine({ ...a, type: bomTypeFor(a?.snapshot?.category || "") }));
  }
  const labels: PinnedDesigns = {};
  for (const [k, v] of Object.entries(c?.labels && typeof c.labels === "object" ? c.labels : {})) {
    const x = v as any;
    if (/^[a-z_]{2,20}$/.test(k) && x?.artworkId) labels[k] = { artworkId: str(x.artworkId, 60), revision: Math.max(0, Math.floor(Number(x.revision) || 0)) };
  }
  return {
    standards: rows(c?.standards).map((x) => str(x, 120)).filter(Boolean),
    generalNotes: str(c?.generalNotes, 5000), fabricNotes: str(c?.fabricNotes, 5000), sizes,
    colourways: rows(c?.colourways).map((r: any) => ({ colour: str(r?.colour, 120).trim(), pantone: str(r?.pantone, 60), notes: str(r?.notes, 500) })).filter((r) => r.colour),
    measureUnit: MEASURE_UNITS.includes(String(c?.measureUnit)) ? String(c.measureUnit) : "cm",
    measurements: measureRows(c?.measurements, sizes, MEASURE_UNITS.includes(String(c?.measureUnit)) ? String(c.measureUnit) : "cm"),
    construction: rows(c?.construction).map((r: any) => ({ area: str(r?.area, 200), seam: str(r?.seam, 200), spi: str(r?.spi, 40), notes: str(r?.notes, 1000) })).filter((r) => r.area || r.seam || r.notes),
    branding: rows(c?.branding).map((r: any) => ({ artwork: str(r?.artwork, 200), method: str(r?.method, 120), position: str(r?.position, 200), dimensions: str(r?.dimensions, 120), pantone: str(r?.pantone, 120), artworkId: str(r?.artworkId, 60), artworkRevision: Math.max(0, Math.floor(Number(r?.artworkRevision) || 0)) })).filter((r) => r.artwork || r.position || r.artworkId),
    bom,
    libraryItems: bom.filter((b) => b.itemId && b.snapshot).map((b) => ({ itemId: b.itemId, revision: b.revision, placement: b.placement, qty: b.qty, notes: b.notes, snapshot: b.snapshot! })),
    packing: c?.packing ? normalizePacking(c.packing) : null,
    packagingNotes: str(c?.packagingNotes, 5000),
    labels,
    folding: { method: str(c?.folding?.method, 80), foldedSize: str(c?.folding?.foldedSize, 80), instructions: str(c?.folding?.instructions, 3000) },
    washCareLabelId: /^[0-9a-f-]{36}$/i.test(String(c?.washCareLabelId || "")) ? String(c.washCareLabelId) : "",
    ...(c?.labelSnapshot && typeof c.labelSnapshot === "object" ? { labelSnapshot: c.labelSnapshot } : {}),
    ...(Array.isArray(c?.bomSnapshot) ? { bomSnapshot: c.bomSnapshot as BomItemView[] } : {}),
    ...(() => {
      const cad = normalizeCad(c?.cad, rows(c?.attachments).map((x) => str(x, 60)).filter(Boolean));
      return { cad, attachments: cadFileIds(cad) };
    })(),
  };
}
/** What changed between two Tech Pack versions (the revision summary), as v2.0.41. */
export function techPackChanges(a: { colour?: string; fabricCode?: string; content: TechPackContent }, b: { colour?: string; fabricCode?: string; content: TechPackContent }): string[] {
  const out: string[] = [];
  const same = (x: unknown, y: unknown) => JSON.stringify(x ?? null) === JSON.stringify(y ?? null);
  if ((a.colour || "") !== (b.colour || "")) out.push("Colour changed");
  if ((a.fabricCode || "") !== (b.fabricCode || "")) out.push("Fabric changed");
  const A = a.content, B = b.content;
  if (A.fabricNotes !== B.fabricNotes) out.push("Fabric specification changed");
  if (!same(A.standards, B.standards)) out.push("Standards changed");
  if (!same(A.colourways, B.colourways)) out.push("Colourways changed");
  if (!same(A.sizes, B.sizes) || !same(A.measurements, B.measurements) || (A.measureUnit || "cm") !== (B.measureUnit || "cm")) out.push("Measurements / grading changed");
  if (!same(A.construction, B.construction)) out.push("Construction details changed");
  if (!same(A.branding, B.branding)) out.push("Branding / artwork changed");
  if (!same(A.bom, B.bom)) out.push("Bill of materials changed");
  if (!same(A.labels, B.labels)) out.push("Label / packaging designs changed");
  if ((A.washCareLabelId || "") !== (B.washCareLabelId || "")) out.push("Wash care label changed");
  if (!same(A.folding, B.folding)) out.push("Folding instructions changed");
  if (!same(A.packing, B.packing)) out.push("Packing specification changed");
  if (A.packagingNotes !== B.packagingNotes) out.push("Packaging instructions changed");
  if (!same(A.cad, B.cad)) out.push("CAD & technical drawings changed");
  if (A.generalNotes !== B.generalNotes) out.push("General notes changed");
  return out;
}
/** Points of measure, with any grading kept (only for the sizes given) and the graded sizes worked out again. */
function measureRows(v: unknown, sizes: string[], unit: string): MeasureRow[] {
  return (Array.isArray(v) ? v : []).map((r: any) => {
    const row: MeasureRow = { pom: str(r?.pom, 200).trim(), tolerance: str(r?.tolerance, 40), values: Object.fromEntries(sizes.map((s) => [s, str(r?.values?.[s], 20)])) };
    const base = str(r?.base, 20);
    if (base && sizes.includes(base)) {
      row.base = base;
      row.grade = str(r?.grade, 20).trim();
      const steps = Object.fromEntries(sizes.filter((s) => s !== base && str(r?.steps?.[s], 20).trim()).map((s) => [s, str(r.steps[s], 20).trim()]));
      if (Object.keys(steps).length) row.steps = steps;
      const manual = sizes.filter((s) => s !== base && Array.isArray(r?.manual) && r.manual.includes(s));
      if (manual.length) row.manual = manual;
    }
    return gradeRow(row, sizes, unit);
  }).filter((r) => r.pom);
}
/** A category's measurement template rows (unit-free; graded sizes are worked out to two decimals). */
export function normalizeTemplateRows(rows: unknown, sizes: string[]): MeasureRow[] {
  return measureRows(rows, sizes, "cm");
}

// ---- R&D ----------------------------------------------------------------------------------------------------------
export type RdStatus = "idea" | "brief" | "fabric" | "sampling" | "testing" | "costing" | "approved" | "rejected" | "hold" | "converted";
export const RD_STATUSES: Array<[RdStatus, string]> = [
  ["idea", "Idea"], ["brief", "Brief Created"], ["fabric", "Fabric Development"], ["sampling", "Sampling"], ["testing", "Testing"],
  ["costing", "Costing Review"], ["approved", "Approved"], ["rejected", "Rejected"], ["hold", "On Hold"], ["converted", "Converted"],
];
export const RD_STATUS_LABEL = Object.fromEntries(RD_STATUSES) as Record<RdStatus, string>;
export const RD_PRIORITIES: Array<[string, string]> = [["low", "Low"], ["normal", "Normal"], ["high", "High"], ["urgent", "Urgent"]];
/** How far along the development is (for the progress bar). */
export function rdProgress(s: RdStatus): number {
  const order: RdStatus[] = ["idea", "brief", "fabric", "sampling", "testing", "costing", "approved"];
  if (s === "converted") return 100;
  const i = order.indexOf(s);
  return i < 0 ? 0 : Math.round(((i + 1) / order.length) * 100);
}
export function nextRdNumber(existing: string[]): string {
  const max = existing.reduce((m, x) => { const r = /^RD(\d+)$/i.exec(String(x).trim()); return r ? Math.max(m, Number(r[1])) : m; }, 0);
  return `RD${String(max + 1).padStart(4, "0")}`;
}
export const RD_TRIAL_RESULTS = ["Pending", "Pass", "Fail", "Approved", "Rejected"];
export const RD_SAMPLE_TYPES = ["Concept Sample", "Prototype", "Fit Sample", "Wear Trial", "PPS"];
export const RD_SAMPLE_RESULTS = ["Pending", "Approved", "Rejected", "Revise"];
export const RD_TEST_RESULTS = ["Pending", "Pass", "Fail"];
